<script setup>
import { computed, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import DateField from 'next/date-field/DateField.vue';
import TimeField from 'next/time-field/TimeField.vue';
import OutlinedSelectField from 'dashboard/components-next/CustomAttributes/OutlinedSelectField.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import CalendarAPI from 'dashboard/api/integrations/calendar';
import { isCancelledEvent } from 'dashboard/helper/appointmentStatus';
import SearchAPI from 'dashboard/api/search';
import ContactAPI from 'dashboard/api/contacts';
import { useAbortableRequest } from 'dashboard/composables/useAbortableRequest';
import { useCalendarTime } from 'dashboard/composables/useCalendarTime';
import { SLOT_MINUTES, addMinutesToTime } from 'dashboard/helper/calendarTime';

import {
  invitationValues,
  renderInvitation,
} from 'dashboard/helper/invitationText';
import {
  calendarSelectOptions,
  connectionSelectOptions,
} from 'dashboard/helper/calendarLabels';

const props = defineProps({
  connections: {
    type: Array,
    default: () => [],
  },
  // An AI bot (Panel AI) is active in some inbox of the account: only then is its follow-up offered.
  panelAiActive: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['saved']);

const { timezone, dateAndTimeParts, rangeFromStartEnd } = useCalendarTime();

const { t, locale } = useI18n();
const currentUser = useMapGetter('getCurrentUser');
const { currentAccount } = useAccount();
const calendarRequest = useAbortableRequest();
const contactRequest = useAbortableRequest();
const conversationRequest = useAbortableRequest();
const invitationRequest = useAbortableRequest();
const lockRequest = useAbortableRequest();
const calendars = ref([]);
const calendarLoadFailed = ref(false);
const loadingCalendars = calendarRequest.isPending;
const isOpen = ref(false);

const dialogRef = ref(null);
const saving = ref(false);
const deleting = ref(false);
const confirmingDelete = ref(false);
const deleteNote = ref('');
const readOnly = ref(false);
const lockHolder = ref('');
const isCreate = ref(true);
const eventId = ref(null);
const etag = ref('');
const connectionId = ref('');
const calendarId = ref('');
const summary = ref('');
const appointmentStatus = ref('confirmed');
const statusOptions = computed(() =>
  ['confirmed', 'pending_confirmation'].map(value => ({
    value,
    label: t(`CONVERSATION_SIDEBAR.CALENDAR.STATUS.${value.toUpperCase()}`),
  }))
);
const statusLabel = computed(() =>
  t(`SIDEBAR.CALENDAR_PAGE.STATUS.${appointmentStatus.value.toUpperCase()}`)
);
const date = ref('');
const time = ref('09:00');
const endTime = ref('09:30');
const tabIndex = ref(0);
const includeMeet = ref(false);
const botFollowupMode = ref('none');
// An event that already carries the bot follow-up keeps showing it, whatever the account has now.
const savedBotFollowup = ref(false);
const showBotFollowup = computed(
  () => props.panelAiActive || savedBotFollowup.value
);
const botFollowupConfirmation = ref(true);
const botFollowupReminder24h = ref(true);
const botFollowupReminder30m = ref(true);
const contactId = ref(null);
const contactLabel = ref('');
const contactQuery = ref('');
const contactResults = ref([]);
const conversationId = ref('');
const conversationQuery = ref('');
const conversationResults = ref([]);
const createdBy = ref('');
const updatedBy = ref('');
const bookingSource = ref('manual');
const isDeleted = ref(false);
const activities = ref([]);
const existingMeet = ref(false);
const conversationLocked = ref(false);
const hideConversation = ref(true);
const contactEmail = ref('');
const inviteEmail = ref('');
const saveEmailOnContact = ref(true);
// The invitation the customer reads: the account's text (settings) unless this appointment has its own. `invitationOwn`
// is set once the agent writes one (or the appointment already had it): only then is it sent with the appointment.
const invitationAccount = ref({ template: '', location: '' });
const invitationText = ref('');
const showInvitationPreview = ref(false);
const invitationOwn = ref(false);
const invitationSaved = ref(false);
const meetLink = ref('');
let heartbeatTimer = null;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const resolveConversationId = (event, defaults) => {
  if (event?.conversation?.id) return String(event.conversation.id);
  if (defaults.conversationId) return String(defaults.conversationId);
  return '';
};

const connectionOptions = computed(() =>
  connectionSelectOptions(props.connections, t)
);

const calendarOptions = computed(() =>
  calendarSelectOptions(calendars.value, t)
);

const toMenuOptions = options =>
  options.map(({ value, label }) => ({ id: value, name: label }));
const selectedMenuOption = (options, value) =>
  toMenuOptions(options).find(option => String(option.id) === String(value)) ||
  null;
const statusMenuOptions = computed(() => toMenuOptions(statusOptions.value));
const selectedStatus = computed(() => ({
  id: appointmentStatus.value,
  name: statusLabel.value,
}));
const connectionMenuOptions = computed(() =>
  toMenuOptions(connectionOptions.value)
);
const selectedConnection = computed(() =>
  selectedMenuOption(connectionOptions.value, connectionId.value)
);
const calendarMenuOptions = computed(() =>
  toMenuOptions(calendarOptions.value)
);
const selectedCalendar = computed(() =>
  selectedMenuOption(calendarOptions.value, calendarId.value)
);

const loadCalendars = async () => {
  calendarRequest.abort();
  calendars.value = [];
  calendarLoadFailed.value = false;
  if (!isOpen.value || !connectionId.value) return;
  try {
    const response = await calendarRequest.run(signal =>
      CalendarAPI.getCalendars(connectionId.value, { signal })
    );
    if (!response) return;
    calendars.value = (response.data.payload || []).filter(
      item => item.enabled !== false
    );
    if (
      isCreate.value &&
      !calendars.value.some(item => item.id === calendarId.value)
    ) {
      calendarId.value = calendars.value[0]?.id || '';
    }
  } catch {
    calendarLoadFailed.value = true;
  }
};

const cancelSearches = () => {
  contactRequest.abort();
  conversationRequest.abort();
  contactResults.value = [];
  conversationResults.value = [];
};

watch(
  connectionId,
  () => {
    cancelSearches();
    if (isCreate.value) calendarId.value = '';
    loadCalendars();
  },
  { flush: 'sync' }
);

const endContext = () => {
  isOpen.value = false;
  calendarRequest.abort();
  invitationRequest.abort();
  lockRequest.abort();
  cancelSearches();
};

const modalTabs = computed(() => [
  { label: t('SIDEBAR.CALENDAR_PAGE.MODAL.TAB_EVENT'), value: 'event' },
  {
    label: t('SIDEBAR.CALENDAR_PAGE.MODAL.TAB_ACTIVITY'),
    value: 'activity',
    count: activities.value.length,
  },
]);

const showEventTab = computed(() => isCreate.value || tabIndex.value === 0);

const showConversationField = computed(
  () => !hideConversation.value && !conversationLocked.value
);

const hasContactEmail = computed(() => Boolean(contactEmail.value));

const attendeeEmail = computed(() => {
  if (contactEmail.value) return contactEmail.value;
  const typed = inviteEmail.value.trim();
  return EMAIL_PATTERN.test(typed) ? typed : '';
});

const title = computed(() =>
  isCreate.value
    ? t('SIDEBAR.CALENDAR_PAGE.MODAL.CREATE_TITLE')
    : t('SIDEBAR.CALENDAR_PAGE.MODAL.EDIT_TITLE')
);

const activityLabel = item => {
  const name = item.user?.name || t('SIDEBAR.CALENDAR_PAGE.MODAL.SYSTEM');
  return t(`SIDEBAR.CALENDAR_PAGE.MODAL.ACTIVITY.${item.action}`, { name });
};

const activityStamp = value => {
  if (!value) return '';
  return new Date(value).toLocaleString(locale.value, {
    timeZone: timezone.value,
    hourCycle: 'h23',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const activityChange = item => {
  if (item.details?.note) return item.details.note;
  const changes = [];
  const times = item.details?.start_at;
  if (Array.isArray(times) && times.length === 2) {
    changes.push(
      t('SIDEBAR.CALENDAR_PAGE.MODAL.TIME_CHANGED', {
        from: activityStamp(times[0]),
        to: activityStamp(times[1]),
      })
    );
  }
  const statuses = item.details?.appointment_status;
  if (Array.isArray(statuses)) {
    changes.push(
      t('SIDEBAR.CALENDAR_PAGE.MODAL.STATUS_CHANGED', {
        from: t(`SIDEBAR.CALENDAR_PAGE.STATUS.${statuses[0].toUpperCase()}`),
        to: t(`SIDEBAR.CALENDAR_PAGE.STATUS.${statuses[1].toUpperCase()}`),
      })
    );
  }
  return changes.join(' \u00b7 ');
};

const activityIcon = action =>
  ({
    created: 'i-lucide-plus',
    updated: 'i-lucide-pencil',
    deleted: 'i-lucide-trash-2',
    moved_in_google: 'i-lucide-calendar',
  })[action] || 'i-lucide-dot';

const originalRange = ref({});
const currentRange = computed(() =>
  rangeFromStartEnd(date.value, time.value, endTime.value, originalRange.value)
);
const invalidRange = computed(() =>
  Boolean(
    date.value &&
      time.value &&
      endTime.value &&
      (!currentRange.value.start ||
        !currentRange.value.end ||
        new Date(currentRange.value.end) <= new Date(currentRange.value.start))
  )
);

const onTabChanged = tab => {
  tabIndex.value = modalTabs.value.findIndex(item => item.value === tab.value);
};

const onStartTimeInput = value => {
  time.value = value;
  if (!endTime.value || endTime.value <= value) {
    endTime.value = addMinutesToTime(value, SLOT_MINUTES);
  }
};

const confirmDisabled = computed(
  () =>
    readOnly.value ||
    loadingCalendars.value ||
    calendarLoadFailed.value ||
    saving.value ||
    !summary.value.trim() ||
    !connectionId.value ||
    !calendarId.value ||
    !date.value ||
    !time.value ||
    !endTime.value ||
    invalidRange.value
);

const invitationShown = computed(() =>
  invitationOwn.value ? invitationText.value : invitationAccount.value.template
);
const invitationPreview = computed(() =>
  renderInvitation(
    invitationShown.value,
    invitationValues({
      contactName: contactLabel.value,
      contactEmail: contactEmail.value,
      agentName: currentUser.value?.name,
      accountName: currentAccount.value?.name,
      date: date.value,
      time: time.value,
      summary: summary.value.trim(),
      location: invitationAccount.value.location,
      meetLink: meetLink.value,
      conversationId: conversationId.value,
      locale: locale.value,
    })
  )
);
const onInvitationInput = value => {
  invitationText.value = value;
  invitationOwn.value = true;
};
const useAccountInvitation = () => {
  invitationText.value = '';
  invitationOwn.value = false;
};

// Only a text written for this appointment travels; nothing means the account's text (which follows a new time).
// Dropping a text it already had sends it empty, which clears it.
const invitationPayload = () => {
  if (invitationOwn.value) return { description: invitationText.value.trim() };
  return invitationSaved.value ? { description: '' } : {};
};

const payload = () => {
  const range = currentRange.value;
  const reminders = [];
  if (botFollowupReminder24h.value) reminders.push(1440);
  if (botFollowupReminder30m.value) reminders.push(30);
  const mode = botFollowupMode.value;
  return {
    connection_id: connectionId.value,
    calendar_id: calendarId.value,
    summary: summary.value.trim(),
    appointment_status: appointmentStatus.value,
    start: range.start,
    end: range.end,
    etag: etag.value,
    contact_id: contactId.value,
    conversation_id: conversationId.value || undefined,
    include_meet: includeMeet.value && !existingMeet.value,
    send_to_contact: mode === 'notice_only',
    bot_followup_policy:
      mode === 'bot_followup'
        ? {
            enabled: true,
            confirmation: botFollowupConfirmation.value,
            reminders_minutes_before: reminders,
            source: 'override',
          }
        : { enabled: false },
    attendee_email: attendeeEmail.value || undefined,
    ...invitationPayload(),
  };
};

const stopHeartbeat = () => {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
};

const releaseLock = async () => {
  endContext();
  stopHeartbeat();
  if (isCreate.value || !eventId.value || readOnly.value) return;
  try {
    await CalendarAPI.unlockEvent(eventId.value, {
      connection_id: connectionId.value,
    });
  } catch {
    // lock TTL covers abandoned edits
  }
};

const startHeartbeat = () => {
  stopHeartbeat();
  heartbeatTimer = setInterval(async () => {
    if (!eventId.value) return;
    try {
      await CalendarAPI.lockEvent(eventId.value, {
        connection_id: connectionId.value,
        heartbeat: true,
      });
    } catch {
      // ignore; save still uses etag
    }
  }, 60000);
};

const applyBotFollowupDefaults = (event, defaults) => {
  const policy = event?.bot_followup_policy;
  savedBotFollowup.value = Boolean(policy?.enabled);
  if (policy?.enabled) {
    botFollowupMode.value = 'bot_followup';
    botFollowupConfirmation.value = policy.confirmation !== false;
    const reminders = Array.isArray(policy.reminders_minutes_before)
      ? policy.reminders_minutes_before
      : [];
    botFollowupReminder24h.value = reminders.includes(1440);
    botFollowupReminder30m.value = reminders.includes(30);
    return;
  }
  const hasConversation = Boolean(resolveConversationId(event, defaults));
  // Panel AI accounts keep the bot follow-up as the default of a new appointment; without Panel AI nothing is sent.
  if (!event && hasConversation && props.panelAiActive) {
    botFollowupMode.value = 'bot_followup';
    botFollowupConfirmation.value = true;
    botFollowupReminder24h.value = true;
    botFollowupReminder30m.value = true;
    return;
  }
  if (defaults.sendToContact) {
    botFollowupMode.value = 'notice_only';
    return;
  }
  botFollowupMode.value = 'none';
};

const applyEvent = (event, defaults) => {
  originalRange.value =
    event && !event.all_day ? { start: event.start, end: event.end } : {};
  isCreate.value = !event;
  eventId.value = event?.id || null;
  etag.value = event?.etag || '';
  connectionId.value = String(
    event?.connection_id ||
      defaults.connectionId ||
      props.connections[0]?.id ||
      ''
  );
  calendarId.value = event?.calendar_id || defaults.calendarId || '';
  summary.value = event?.summary || '';
  const parts = dateAndTimeParts(event?.start || defaults.start);
  date.value = parts.date;
  time.value = parts.time;
  endTime.value = event
    ? dateAndTimeParts(event.end).time
    : addMinutesToTime(parts.time, defaults.duration || SLOT_MINUTES);
  includeMeet.value = Boolean(event?.meet_link);
  existingMeet.value = Boolean(event?.meet_link);
  meetLink.value = event?.meet_link || '';
  invitationText.value = event?.invitation_text || '';
  showInvitationPreview.value = false;
  invitationOwn.value = Boolean(event?.invitation_text);
  invitationSaved.value = Boolean(event?.invitation_text);
  contactId.value = event?.contact?.id || defaults.contactId || null;
  contactLabel.value = event?.contact?.name || defaults.contactName || '';
  contactQuery.value = contactLabel.value;
  conversationId.value = resolveConversationId(event, defaults);
  conversationLocked.value = Boolean(defaults.conversationId);
  hideConversation.value =
    Boolean(defaults.hideConversation) && !defaults.conversationId;
  applyBotFollowupDefaults(event, defaults);
  appointmentStatus.value =
    event?.appointment_status ||
    (botFollowupMode.value === 'bot_followup' && botFollowupConfirmation.value
      ? 'pending_confirmation'
      : 'confirmed');
  contactEmail.value = event?.contact?.email || defaults.contactEmail || '';
  inviteEmail.value = contactEmail.value;
  saveEmailOnContact.value = !contactEmail.value;
  createdBy.value = event?.created_by?.name || '';
  updatedBy.value = event?.updated_by?.name || '';
  bookingSource.value = event?.booking_source || 'manual';
  activities.value = event?.activities || [];
  isDeleted.value = isCancelledEvent(event);
  deleteNote.value = event?.deleted_note || '';
  confirmingDelete.value = false;
  readOnly.value = isDeleted.value;
  lockHolder.value = event?.deleted_by?.name || '';
  tabIndex.value = isDeleted.value ? 1 : 0;
};

const acquireLock = async () => {
  if (isCreate.value || !eventId.value || isDeleted.value) return;
  try {
    const acquired = await lockRequest.run(async signal => {
      await CalendarAPI.lockEvent(
        eventId.value,
        {
          connection_id: connectionId.value,
        },
        { signal }
      );
      return true;
    });
    if (!acquired) return;
    startHeartbeat();
  } catch (error) {
    if (error.response?.status === 423) {
      readOnly.value = true;
      lockHolder.value = error.response?.data?.payload?.name || '';
      useAlert(
        error.response?.data?.error ||
          t('SIDEBAR.CALENDAR_PAGE.MODAL.LOCKED', { name: lockHolder.value })
      );
      return;
    }
    useAlert(t('SIDEBAR.CALENDAR_PAGE.MODAL.LOCK_ERROR'));
  }
};

const loadInvitation = async () => {
  try {
    const response = await invitationRequest.run(signal =>
      CalendarAPI.getInvitation({ signal })
    );
    if (!response) return;
    const { data } = response;
    invitationAccount.value = {
      template: data.template || data.default_template || '',
      location: data.location || '',
    };
  } catch {
    invitationAccount.value = { template: '', location: '' };
  }
};

const open = async ({ event = null, defaults = {} } = {}) => {
  endContext();
  stopHeartbeat();
  applyEvent(event, defaults);
  isOpen.value = true;
  loadCalendars();
  loadInvitation();
  if (defaults.dateKey) {
    date.value = defaults.dateKey;
    time.value = `${String(defaults.hours ?? 9).padStart(2, '0')}:${String(defaults.minutes ?? 0).padStart(2, '0')}`;
    endTime.value = addMinutesToTime(time.value, SLOT_MINUTES);
  }
  if (defaults.startIso) {
    const parts = dateAndTimeParts(defaults.startIso);
    date.value = parts.date;
    time.value = parts.time;
    endTime.value = addMinutesToTime(parts.time, SLOT_MINUTES);
  }
  dialogRef.value?.open();
  await acquireLock();
};

const close = async () => {
  await releaseLock();
  dialogRef.value?.close();
};

const onSearchContacts = async query => {
  cancelSearches();
  contactQuery.value = query;
  if (query !== contactLabel.value) {
    contactId.value = null;
    contactLabel.value = '';
    contactEmail.value = '';
  }
  if (query.trim().length < 2) return;
  try {
    const response = await contactRequest.run(signal =>
      ContactAPI.search(query.trim(), 1, 'name', '', { signal })
    );
    if (!response) return;
    contactResults.value = response.data.payload || [];
  } catch {
    useAlert(t('SIDEBAR.CALENDAR_PAGE.LOAD_ERROR'));
  }
};

const selectContact = contact => {
  cancelSearches();
  contactId.value = contact.id;
  contactLabel.value = contact.name;
  contactQuery.value = contact.name;
  contactEmail.value = contact.email || '';
  inviteEmail.value = contact.email || '';
  saveEmailOnContact.value = !contact.email;
  contactResults.value = [];
};

const onSearchConversations = async query => {
  conversationRequest.abort();
  conversationResults.value = [];
  conversationQuery.value = query;
  if (!query || query.length < 2) {
    conversationResults.value = [];
    return;
  }
  try {
    const response = await conversationRequest.run(signal =>
      SearchAPI.conversations({ q: query }, { signal })
    );
    if (!response) return;
    const { data } = response;
    conversationResults.value = data.payload?.conversations || [];
  } catch {
    conversationResults.value = [];
  }
};

const selectConversation = conversation => {
  cancelSearches();
  conversationId.value = String(conversation.id);
  conversationQuery.value = `#${conversation.id}`;
  conversationResults.value = [];
  if (conversation.contact && !contactId.value) {
    contactId.value = conversation.contact.id;
    contactLabel.value = conversation.contact.name;
    contactQuery.value = conversation.contact.name;
    contactEmail.value = conversation.contact.email || '';
    inviteEmail.value = contactEmail.value;
    saveEmailOnContact.value = !contactEmail.value;
  }
};

const persistContactEmail = async () => {
  if (
    !contactId.value ||
    contactEmail.value ||
    !saveEmailOnContact.value ||
    !attendeeEmail.value
  ) {
    return;
  }
  await ContactAPI.update(contactId.value, { email: attendeeEmail.value });
  contactEmail.value = attendeeEmail.value;
};

const save = async () => {
  if (confirmDisabled.value) return;
  saving.value = true;
  try {
    await persistContactEmail();
    let savedEvent = null;
    if (isCreate.value) {
      const { data } = await CalendarAPI.createEvent(payload());
      savedEvent = data.payload;
      useAlert(t('SIDEBAR.CALENDAR_PAGE.MODAL.CREATE_SUCCESS'));
    } else {
      const { data } = await CalendarAPI.updateEvent(eventId.value, payload());
      savedEvent = data.payload;
      useAlert(t('SIDEBAR.CALENDAR_PAGE.MODAL.UPDATE_SUCCESS'));
    }
    emit('saved', savedEvent);
    await close();
  } catch (error) {
    useAlert(
      error.response?.data?.error || t('SIDEBAR.CALENDAR_PAGE.MODAL.SAVE_ERROR')
    );
  } finally {
    saving.value = false;
  }
};

const remove = async () => {
  if (isCreate.value || readOnly.value) return;
  if (!confirmingDelete.value) {
    confirmingDelete.value = true;
    return;
  }
  if (!deleteNote.value.trim()) {
    useAlert(t('SIDEBAR.CALENDAR_PAGE.MODAL.DELETE_NOTE_REQUIRED'));
    return;
  }
  deleting.value = true;
  try {
    const { data } = await CalendarAPI.deleteEvent(eventId.value, {
      connection_id: connectionId.value,
      calendar_id: calendarId.value,
      etag: etag.value,
      note: deleteNote.value.trim(),
    });
    useAlert(t('SIDEBAR.CALENDAR_PAGE.MODAL.DELETE_SUCCESS'));
    emit('saved', {
      ...(data?.payload || { id: eventId.value }),
      deleted: true,
    });
    confirmingDelete.value = false;
    await close();
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        t('SIDEBAR.CALENDAR_PAGE.MODAL.DELETE_ERROR')
    );
  } finally {
    deleting.value = false;
  }
};

onUnmounted(() => {
  endContext();
  stopHeartbeat();
});

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="lg"
    body-scroll
    :title="title"
    :is-loading="saving"
    :disable-confirm-button="confirmDisabled"
    :confirm-button-label="$t('SIDEBAR.CALENDAR_PAGE.MODAL.SAVE')"
    :show-confirm-button="!readOnly && showEventTab && !confirmingDelete"
    @confirm="save"
    @close="releaseLock"
  >
    <div class="flex flex-col gap-4">
      <p
        v-if="isDeleted"
        class="rounded-lg bg-n-ruby-3/80 px-3 py-2 text-sm text-n-ruby-11"
      >
        <span class="block font-medium">
          {{ $t('SIDEBAR.CALENDAR_PAGE.DELETED') }}
        </span>
        <span v-if="lockHolder" class="block text-xs mt-0.5">
          {{
            $t('SIDEBAR.CALENDAR_PAGE.MODAL.DELETED_BY', { name: lockHolder })
          }}
        </span>
        <span v-if="deleteNote" class="block text-xs mt-0.5">
          {{ deleteNote }}
        </span>
      </p>
      <p
        v-else-if="readOnly"
        class="rounded-lg bg-n-amber-3 px-3 py-2 text-sm text-n-amber-11"
      >
        {{ $t('SIDEBAR.CALENDAR_PAGE.MODAL.LOCKED', { name: lockHolder }) }}
      </p>
      <TabBar
        v-if="!isCreate"
        :tabs="modalTabs"
        :initial-active-tab="tabIndex"
        @tab-changed="onTabChanged"
      />
      <template v-if="showEventTab">
        <Input
          v-model="summary"
          :label="$t('SIDEBAR.CALENDAR_PAGE.MODAL.TITLE')"
          :placeholder="$t('SIDEBAR.CALENDAR_PAGE.MODAL.TITLE_PLACEHOLDER')"
          :disabled="readOnly"
        />
        <OutlinedSelectField
          :label="$t('SIDEBAR.CALENDAR_PAGE.MODAL.APPOINTMENT_STATUS')"
          :options="statusMenuOptions"
          :selected-item="selectedStatus"
          :show-search="false"
          :disabled="readOnly"
          data-testid="appointment-status"
          @select="option => (appointmentStatus = option.id)"
        />
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <OutlinedSelectField
            :label="$t('SIDEBAR.CALENDAR_PAGE.ACCOUNT')"
            :options="connectionMenuOptions"
            :selected-item="selectedConnection"
            :show-search="false"
            :disabled="readOnly || !isCreate"
            data-testid="appointment-connection"
            @select="option => (connectionId = String(option.id))"
          />
          <OutlinedSelectField
            :label="$t('SIDEBAR.CALENDAR_PAGE.CALENDAR')"
            :options="calendarMenuOptions"
            :selected-item="selectedCalendar"
            :show-search="false"
            :disabled="
              readOnly || !isCreate || loadingCalendars || calendarLoadFailed
            "
            data-testid="appointment-calendar"
            @select="option => (calendarId = option.id)"
          />
        </div>
        <p
          v-if="loadingCalendars"
          role="status"
          class="m-0 text-sm text-n-slate-11"
        >
          {{ $t('SIDEBAR.CALENDAR_PAGE.MODAL.LOADING_CALENDARS') }}
        </p>
        <div
          v-else-if="calendarLoadFailed"
          role="alert"
          class="flex items-center gap-2 text-sm text-n-ruby-11"
        >
          <span>{{
            $t('SIDEBAR.CALENDAR_PAGE.MODAL.CALENDAR_LOAD_ERROR')
          }}</span>
          <Button
            sm
            faded
            slate
            :label="$t('SIDEBAR.CALENDAR_PAGE.MODAL.RETRY_CALENDARS')"
            @click="loadCalendars"
          />
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <DateField
            v-model="date"
            :label="$t('SIDEBAR.CALENDAR_PAGE.MODAL.DATE')"
            :disabled="readOnly"
          />
          <TimeField
            :model-value="time"
            :label="$t('SIDEBAR.CALENDAR_PAGE.MODAL.START_TIME')"
            :disabled="readOnly"
            @update:model-value="onStartTimeInput"
          />
          <TimeField
            v-model="endTime"
            :label="$t('SIDEBAR.CALENDAR_PAGE.MODAL.END_TIME')"
            :disabled="readOnly"
            :message="
              invalidRange
                ? $t('SIDEBAR.CALENDAR_PAGE.MODAL.INVALID_RANGE')
                : ''
            "
            :message-type="invalidRange ? 'error' : 'info'"
          />
        </div>
        <div class="relative">
          <Input
            :model-value="contactQuery"
            :label="$t('SIDEBAR.CALENDAR_PAGE.MODAL.CONTACT')"
            :placeholder="$t('SIDEBAR.CALENDAR_PAGE.MODAL.CONTACT_PLACEHOLDER')"
            :disabled="readOnly"
            @update:model-value="onSearchContacts"
          />
          <ul
            v-if="contactResults.length"
            class="absolute z-20 mt-1 w-full list-none m-0 p-0 rounded-lg border border-n-weak bg-n-solid-2 max-h-40 overflow-auto"
          >
            <li v-for="contact in contactResults" :key="contact.id">
              <button
                type="button"
                class="w-full px-3 py-2 text-left text-sm hover:bg-n-alpha-2"
                @click="selectContact(contact)"
              >
                <span class="block truncate">{{ contact.name }}</span>
                <span
                  v-if="contact.email"
                  class="block text-xs text-n-slate-11 truncate"
                >
                  {{ contact.email }}
                </span>
              </button>
            </li>
          </ul>
        </div>
        <div v-if="!contactId" class="flex flex-col gap-1">
          <Input
            v-model="inviteEmail"
            type="email"
            :label="$t('SIDEBAR.CALENDAR_PAGE.MODAL.INVITE_EMAIL')"
            :placeholder="
              $t('SIDEBAR.CALENDAR_PAGE.MODAL.INVITE_EMAIL_PLACEHOLDER')
            "
            :disabled="readOnly"
          />
        </div>
        <p
          v-if="contactId && hasContactEmail"
          class="rounded-lg bg-n-teal-3/50 px-3 py-2 text-sm text-n-teal-11"
        >
          {{ $t('SIDEBAR.CALENDAR_PAGE.MODAL.CONTACT_EMAIL') }}
          {{ contactEmail }}
        </p>
        <div
          v-else-if="contactId && !hasContactEmail"
          class="flex flex-col gap-2 rounded-lg border border-n-amber-6 bg-n-amber-3/40 px-3 py-3"
        >
          <p class="text-sm text-n-amber-11">
            {{ $t('SIDEBAR.CALENDAR_PAGE.MODAL.NO_EMAIL') }}
          </p>
          <Input
            v-model="inviteEmail"
            type="email"
            :label="$t('SIDEBAR.CALENDAR_PAGE.MODAL.INVITE_EMAIL')"
            :placeholder="
              $t('SIDEBAR.CALENDAR_PAGE.MODAL.INVITE_EMAIL_PLACEHOLDER')
            "
            :disabled="readOnly"
          />
          <label class="flex items-center gap-2 text-sm text-n-slate-12">
            <Checkbox v-model="saveEmailOnContact" :disabled="readOnly" />
            {{ $t('SIDEBAR.CALENDAR_PAGE.MODAL.SAVE_EMAIL_ON_CONTACT') }}
          </label>
        </div>
        <div v-if="showConversationField" class="relative">
          <Input
            :model-value="
              conversationQuery || (conversationId ? `#${conversationId}` : '')
            "
            :label="$t('SIDEBAR.CALENDAR_PAGE.MODAL.CONVERSATION')"
            :placeholder="
              $t('SIDEBAR.CALENDAR_PAGE.MODAL.CONVERSATION_PLACEHOLDER')
            "
            :disabled="readOnly"
            @update:model-value="onSearchConversations"
          />
          <ul
            v-if="conversationResults.length"
            class="absolute z-20 mt-1 w-full list-none m-0 p-0 rounded-lg border border-n-weak bg-n-solid-2 max-h-40 overflow-auto"
          >
            <li
              v-for="conversation in conversationResults"
              :key="conversation.id"
            >
              <button
                type="button"
                class="w-full px-3 py-2 text-left text-sm hover:bg-n-alpha-2"
                @click="selectConversation(conversation)"
              >
                #{{ conversation.id }}
                {{ conversation.contact?.name }}
              </button>
            </li>
          </ul>
        </div>
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <Checkbox
            v-model="includeMeet"
            :disabled="readOnly || existingMeet"
          />
          {{ $t('SIDEBAR.CALENDAR_PAGE.MODAL.MEET') }}
        </label>
        <div class="flex flex-col gap-1.5" data-testid="invitation-section">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('CALENDAR_INVITATION.MODAL.LABEL') }}
          </span>
          <p class="m-0 text-xs text-n-slate-11">
            {{ $t('CALENDAR_INVITATION.MODAL.HELP') }}
          </p>
          <TextArea
            auto-height
            resize
            min-height="8rem"
            max-height="24rem"
            :model-value="invitationShown"
            :disabled="readOnly"
            :max-length="4000"
            @update:model-value="onInvitationInput"
          />
          <div v-if="invitationOwn" class="flex items-center gap-2">
            <span class="text-xs text-n-slate-11">
              {{ $t('CALENDAR_INVITATION.MODAL.CUSTOM') }}
            </span>
            <Button
              type="button"
              ghost
              slate
              xs
              :label="$t('CALENDAR_INVITATION.MODAL.USE_ACCOUNT')"
              :disabled="readOnly"
              data-testid="invitation-use-account"
              @click="useAccountInvitation"
            />
          </div>
          <Button
            type="button"
            ghost
            slate
            xs
            :icon="showInvitationPreview ? 'i-lucide-eye-off' : 'i-lucide-eye'"
            :label="
              showInvitationPreview
                ? $t('CALENDAR_INVITATION.MODAL.HIDE_PREVIEW')
                : $t('CALENDAR_INVITATION.MODAL.SHOW_PREVIEW')
            "
            :aria-expanded="showInvitationPreview"
            data-testid="invitation-preview-toggle"
            @click="showInvitationPreview = !showInvitationPreview"
          />
          <p
            v-if="showInvitationPreview"
            class="p-2.5 m-0 text-sm whitespace-pre-wrap border rounded-lg font-sans border-n-weak bg-n-alpha-2 text-n-slate-12"
            data-testid="invitation-preview"
          >
            {{ invitationPreview }}
          </p>
          <span v-if="includeMeet && !meetLink" class="text-xs text-n-slate-11">
            {{ $t('CALENDAR_INVITATION.MODAL.MEET_NOTE') }}
          </span>
        </div>
        <fieldset
          v-if="conversationId"
          class="flex flex-col gap-2 m-0 p-0 border-0"
        >
          <legend class="mb-0.5 text-sm font-medium text-n-slate-12">
            {{ $t('SIDEBAR.CALENDAR_PAGE.MODAL.FOLLOWUP_TITLE') }}
          </legend>
          <p class="m-0 text-xs text-n-slate-11">
            {{ $t('SIDEBAR.CALENDAR_PAGE.MODAL.FOLLOWUP_CAPTAIN_NOTE') }}
          </p>
          <label class="flex items-start gap-2 text-sm text-n-slate-12">
            <input
              v-model="botFollowupMode"
              type="radio"
              value="none"
              class="mt-1 accent-n-brand"
              :disabled="readOnly"
            />
            <span class="flex flex-col">
              {{ $t('SIDEBAR.CALENDAR_PAGE.MODAL.FOLLOWUP_NONE') }}
              <span class="text-xs text-n-slate-11">
                {{ $t('SIDEBAR.CALENDAR_PAGE.MODAL.FOLLOWUP_NONE_HELP') }}
              </span>
            </span>
          </label>
          <label class="flex items-start gap-2 text-sm text-n-slate-12">
            <input
              v-model="botFollowupMode"
              type="radio"
              value="notice_only"
              class="mt-1 accent-n-brand"
              :disabled="readOnly"
            />
            <span class="flex flex-col">
              {{ $t('SIDEBAR.CALENDAR_PAGE.MODAL.FOLLOWUP_NOTICE') }}
              <span class="text-xs text-n-slate-11">
                {{ $t('SIDEBAR.CALENDAR_PAGE.MODAL.FOLLOWUP_NOTICE_HELP') }}
              </span>
            </span>
          </label>
          <label
            v-if="showBotFollowup"
            class="flex items-start gap-2 text-sm text-n-slate-12"
          >
            <input
              v-model="botFollowupMode"
              type="radio"
              value="bot_followup"
              class="mt-1 accent-n-brand"
              :disabled="readOnly"
            />
            <span class="flex flex-col">
              {{ $t('SIDEBAR.CALENDAR_PAGE.MODAL.FOLLOWUP_BOT') }}
              <span class="text-xs text-n-slate-11">
                {{ $t('SIDEBAR.CALENDAR_PAGE.MODAL.FOLLOWUP_BOT_HELP') }}
              </span>
            </span>
          </label>
          <div
            v-if="showBotFollowup && botFollowupMode === 'bot_followup'"
            class="ml-6 flex flex-col gap-2"
          >
            <label class="flex items-center gap-2 text-sm text-n-slate-12">
              <Checkbox
                v-model="botFollowupConfirmation"
                :disabled="readOnly"
              />
              {{ $t('SIDEBAR.CALENDAR_PAGE.MODAL.FOLLOWUP_CONFIRMATION') }}
            </label>
            <label class="flex items-center gap-2 text-sm text-n-slate-12">
              <Checkbox v-model="botFollowupReminder24h" :disabled="readOnly" />
              {{ $t('SIDEBAR.CALENDAR_PAGE.MODAL.FOLLOWUP_REMINDER_24H') }}
            </label>
            <label class="flex items-center gap-2 text-sm text-n-slate-12">
              <Checkbox v-model="botFollowupReminder30m" :disabled="readOnly" />
              {{ $t('SIDEBAR.CALENDAR_PAGE.MODAL.FOLLOWUP_REMINDER_30M') }}
            </label>
          </div>
        </fieldset>
      </template>
      <div v-else class="min-h-64 pt-1">
        <p
          v-if="createdBy || updatedBy || bookingSource === 'ai'"
          class="mb-4 text-sm text-n-slate-11"
        >
          <span
            v-if="bookingSource === 'ai'"
            class="mb-1 inline-flex items-center gap-1 rounded-full bg-n-teal-3 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-n-teal-11"
          >
            <span class="i-lucide-sparkles size-2.5" />
            {{ $t('SIDEBAR.CALENDAR_PAGE.MODAL.AI_BOOKED') }}
          </span>
          <span v-if="createdBy" class="block">
            {{
              $t('SIDEBAR.CALENDAR_PAGE.MODAL.CREATED_BY', { name: createdBy })
            }}
          </span>
          <span v-if="updatedBy" class="block text-xs text-n-slate-10">
            {{
              $t('SIDEBAR.CALENDAR_PAGE.MODAL.UPDATED_BY', { name: updatedBy })
            }}
          </span>
        </p>
        <p
          v-if="!activities.length"
          class="py-10 text-sm text-center text-n-slate-11"
        >
          {{ $t('SIDEBAR.CALENDAR_PAGE.MODAL.EMPTY_ACTIVITY') }}
        </p>
        <ol v-else class="flex flex-col list-none m-0 p-0">
          <li
            v-for="(item, index) in activities"
            :key="item.id"
            class="flex gap-3"
          >
            <div class="flex flex-col items-center">
              <span
                class="flex size-8 shrink-0 items-center justify-center rounded-full bg-n-blue-3"
              >
                <Icon
                  :icon="activityIcon(item.action)"
                  class="size-3.5 text-n-blue-11"
                />
              </span>
              <span
                v-if="index < activities.length - 1"
                class="w-px flex-1 bg-n-weak my-1"
              />
            </div>
            <div
              class="min-w-0 pb-5"
              :class="{ 'pb-0': index === activities.length - 1 }"
            >
              <p class="pt-1.5 text-sm font-medium text-n-slate-12">
                {{ activityLabel(item) }}
              </p>
              <p
                v-if="activityChange(item)"
                class="mt-0.5 text-xs text-n-slate-11"
              >
                {{ activityChange(item) }}
              </p>
              <p class="mt-0.5 text-xs text-n-slate-10">
                {{ activityStamp(item.created_at) }}
              </p>
            </div>
          </li>
        </ol>
      </div>
    </div>
    <template v-if="confirmingDelete" #footer>
      <div class="flex flex-col w-full gap-3">
        <Input
          v-model="deleteNote"
          :label="$t('SIDEBAR.CALENDAR_PAGE.MODAL.DELETE_NOTE')"
          :placeholder="
            $t('SIDEBAR.CALENDAR_PAGE.MODAL.DELETE_NOTE_PLACEHOLDER')
          "
        />
        <div class="flex items-center justify-between w-full gap-3">
          <Button
            faded
            slate
            class="w-full"
            :label="$t('DIALOG.BUTTONS.CANCEL')"
            type="button"
            @click="confirmingDelete = false"
          />
          <Button
            ruby
            class="w-full"
            :label="$t('SIDEBAR.CALENDAR_PAGE.MODAL.DELETE_CONFIRM')"
            :is-loading="deleting"
            :disabled="!deleteNote.trim()"
            type="button"
            @click="remove"
          />
        </div>
      </div>
    </template>
    <template v-else-if="!isCreate && !readOnly && showEventTab" #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          faded
          ruby
          class="w-full"
          :label="$t('SIDEBAR.CALENDAR_PAGE.MODAL.DELETE')"
          :is-loading="deleting"
          type="button"
          @click="remove"
        />
        <Button
          faded
          slate
          class="w-full"
          :label="$t('DIALOG.BUTTONS.CANCEL')"
          type="button"
          @click="close"
        />
        <Button
          blue
          class="w-full"
          :label="$t('SIDEBAR.CALENDAR_PAGE.MODAL.SAVE')"
          :is-loading="saving"
          :disabled="confirmDisabled"
          type="button"
          @click="save"
        />
      </div>
    </template>
  </Dialog>
</template>
